/**
 * `/contacts` — the real list (design §2.4).
 *
 * Filters live in the URL; the request builder sends only what the server
 * honours (see `api/queries.ts` for the verified table). The three empty states
 * the design demands — "no contacts", "filter matches nothing", "request failed"
 * — are distinguished here, because an "add your first contact" prompt shown
 * after a failed request is a lie about the state of someone's data.
 */

import { Button, EmptyState } from '@crm/ui';
import { createFileRoute, Link } from '@tanstack/react-router';
import { AlertTriangleIcon, FilterXIcon, UsersIcon } from 'lucide-react';
import { useMemo } from 'react';
import { useContactListQuery, useListsQuery } from '../../../api/queries.ts';
import {
  ContactFilterBar,
  useContactFilterNavigation,
} from '../../../components/contacts/contact-filter-bar.tsx';
import { ContactTable } from '../../../components/contacts/contact-table.tsx';
import { PageHeader } from '../../../components/page.tsx';
import { RouteErrorPanel } from '../../../components/route-error-boundary.tsx';
import { toClientError } from '../../../lib/errors.ts';
import { parseContactsSearch } from '../../../lib/search.ts';

export const Route = createFileRoute('/_authed/contacts/')({
  validateSearch: parseContactsSearch,
  component: ContactsPage,
  errorComponent: RouteErrorPanel,
});

function ContactsPage() {
  const search = Route.useSearch();
  const onChange = useContactFilterNavigation(search);

  const contactsQuery = useContactListQuery(search);
  const listsQuery = useListsQuery();

  const contacts = useMemo(
    () => contactsQuery.data?.pages.flatMap((page) => page.items) ?? [],
    [contactsQuery.data],
  );

  const filtered =
    search.type !== undefined || search.listId !== undefined || search.source !== 'all';
  const error = contactsQuery.error === null ? null : toClientError(contactsQuery.error);

  return (
    <>
      <PageHeader
        title="Contacts"
        description="Sorted by most recently updated — the only order the server paginates correctly today."
        actions={
          <Button asChild size="sm">
            <Link to="/contacts/new">Add contact</Link>
          </Button>
        }
      />

      <ContactFilterBar
        value={search}
        onChange={onChange}
        lists={listsQuery.data?.items ?? []}
        listsDisabled={listsQuery.isLoading}
        {...(contactsQuery.isSuccess ? { totalLabel: `${contacts.length} loaded` } : {})}
      />

      {error !== null ? (
        <EmptyState
          kind="error"
          className="m-4"
          icon={<AlertTriangleIcon aria-hidden />}
          title="Could not load contacts"
          description={
            <>
              <p>{error.message}</p>
              {error.correlationId === null ? null : (
                <p className="mt-1 font-mono text-xs">correlation {error.correlationId}</p>
              )}
            </>
          }
          action={
            <Button size="sm" variant="outline" onClick={() => void contactsQuery.refetch()}>
              Try again
            </Button>
          }
        />
      ) : !contactsQuery.isLoading && contacts.length === 0 && filtered ? (
        <EmptyState
          kind="filtered"
          className="m-4"
          icon={<FilterXIcon aria-hidden />}
          title="No contacts match these filters"
          description="Your contacts are still there — this view just excludes all of them."
          action={
            <Button
              size="sm"
              variant="outline"
              onClick={() => onChange({ type: undefined, listId: undefined, source: 'all' })}
            >
              Clear filters
            </Button>
          }
        />
      ) : !contactsQuery.isLoading && contacts.length === 0 ? (
        <EmptyState
          className="m-4"
          icon={<UsersIcon aria-hidden />}
          title="No contacts yet"
          description="Add the last person you met. It takes about fifteen seconds."
          action={
            <Button asChild size="sm">
              <Link to="/contacts/new">Add contact</Link>
            </Button>
          }
        />
      ) : (
        <ContactTable
          contacts={contacts}
          isLoading={contactsQuery.isLoading}
          hasNextPage={contactsQuery.hasNextPage}
          isFetchingNextPage={contactsQuery.isFetchingNextPage}
          onLoadMore={() => void contactsQuery.fetchNextPage()}
        />
      )}
    </>
  );
}
